/**
 * Helpers for parsing inline static content JSON stored in the aggregate `content` field.
 */

export type StaticContentStorageReference = {
  contentUrl: string;
};

export type ParsedStaticContentReference = {
  contentUrl?: string;
  inline?: unknown;
};

export class StaticContentStorageReferenceError extends Error {
  constructor(contentUrl: string) {
    super(
      `Static content is stored as an external reference (${contentUrl}). Migrate it to inline JSON before build or studio use.`,
    );
    this.name = 'StaticContentStorageReferenceError';
  }
}

export function parseStaticContentReference(content: string): ParsedStaticContentReference {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'contentUrl' in parsed &&
      typeof (parsed as StaticContentStorageReference).contentUrl === 'string' &&
      (parsed as StaticContentStorageReference).contentUrl
    ) {
      return { contentUrl: (parsed as StaticContentStorageReference).contentUrl };
    }
    return { inline: parsed };
  } catch {
    return { inline: content };
  }
}

export function wrapStaticContentReference(contentUrl: string): string {
  return JSON.stringify({ contentUrl } satisfies StaticContentStorageReference);
}

/** Parses inline JSON from the aggregate `content` field. Legacy `{ contentUrl }` refs throw. */
export function parseStaticContentPayload(content: string): unknown {
  const { contentUrl, inline } = parseStaticContentReference(content);
  if (contentUrl) {
    throw new StaticContentStorageReferenceError(contentUrl);
  }
  return inline;
}

export function resolveStaticContentPayload(content: string): unknown {
  return parseStaticContentPayload(content);
}

export function resolveStaticContentItems(
  items: Array<{ content: string }> | undefined,
): unknown[] {
  if (!items?.length) return [];

  return items
    .map(item => {
      try {
        return parseStaticContentPayload(item.content);
      } catch {
        return null;
      }
    })
    .filter((item): item is unknown => item != null);
}

export function buildStaticContentFileName(
  contentType: string,
  entityId: string,
  suffix = 'json',
): string {
  const safeType = contentType.toLowerCase().replace(/[^a-z0-9-]+/g, '-');
  const safeId = entityId.replace(/[^a-zA-Z0-9-]+/g, '-');
  return `${safeType}-${safeId}-${Date.now()}.${suffix}`;
}
